/* Endnotes own their references, numbering and cleanup. No mutation of theme navigation or cart. */
(() => {
  if (customElements.get('ys-t5-footnotes')) return;
  class T5Footnotes extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.main = this.closest('main');
      if (!this.main) return;
      this.references = [];
      this.tails = [];
      const schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => { if (this.isConnected) this.build(); });
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, {once:true,signal});
      else schedule();
      for (const event of ['shopify:section:load','shopify:section:unload','shopify:section:reorder']) document.addEventListener(event, schedule, {signal});
      this.main.addEventListener('click', event => this.navigate(event), {signal});
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.clear();
    }

    clear() {
      for (const ref of this.references || []) ref.remove();
      for (const tail of this.tails || []) {
        const parent = tail.parentNode;
        if (parent) { tail.replaceWith(...tail.childNodes); parent.normalize(); }
      }
      this.references = [];
      this.tails = [];
      this.querySelectorAll('[data-note-back]').forEach(back => {
        back.hidden = true;
        back.removeAttribute('href');
      });
    }

    tailFor(target, phrase = '') {
      const placement = phrase ? `text:${phrase}` : 'end';
      let tail = [...target.querySelectorAll('.ys-fn-tail')].find(e => e.dataset.noteOwner === this.id && e.dataset.notePlacement === placement);
      if (tail) return tail;
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, {
        acceptNode: node => node.textContent.trim() && !node.parentElement.closest('a,button,sup,script,style,.ys-fn-mark') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
      });
      let last = null;
      let phraseEnd = 0;
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!phrase) last = node;
        else {
          const index = node.textContent.indexOf(phrase);
          if (index >= 0) { last = node; phraseEnd = index + phrase.length; break; }
        }
      }
      if (phrase && !last) return this.tailFor(target);
      if (!last) return null;
      if (phrase && phraseEnd < last.textContent.length) last.splitText(phraseEnd);
      const match = last.textContent.match(/\S+\s*$/);
      if (!match) return null;
      const word = match.index === 0 ? last : last.splitText(match.index);
      tail = document.createElement('span');
      tail.className = 'ys-fn-tail';
      tail.dataset.noteOwner = this.id;
      tail.dataset.notePlacement = placement;
      word.replaceWith(tail);
      tail.append(word);
      this.tails.push(tail);
      return tail;
    }

    build() {
      this.clear();
      const warnings = [];
      const seenKeys = new Set();
      for (const note of this.querySelectorAll('[data-note-number]')) {
        const number = note.dataset.noteNumber;
        const key = note.dataset.noteKey;
        if (key && seenKeys.has(key)) warnings.push(`Repeated record key: ${key}`);
        seenKeys.add(key);
        const selectors = (note.dataset.noteTargets || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
        const targets = new Set();
        for (const selector of selectors) {
          try {
            const matches = this.main.querySelectorAll(selector);
            if (!matches.length) warnings.push(`Note ${number}: target not found (${selector})`);
            if (matches.length > 24) { warnings.push(`Note ${number}: selector is too broad`); continue; }
            for (const target of matches) {
              if (target.closest('ys-t5-footnotes,a,button,[role="button"],[role="tab"],form') || !/^(H[1-6]|P|SPAN|B|STRONG|DD|DT|TH)$/.test(target.tagName)) {
                warnings.push(`Note ${number}: choose a non-interactive text target`);
                continue;
              }
              targets.add(target);
            }
          } catch (_error) { warnings.push(`Note ${number}: invalid selector (${selector})`); }
        }
        const back = note.querySelector('[data-note-back]');
        let index = 0;
        for (const target of targets) {
          // A second note module must not append competing marker sets to the same text.
          if ([...target.querySelectorAll('.ys-fn-tail')].some(e => e.dataset.noteOwner !== this.id)) continue;
          const tail = this.tailFor(target, note.dataset.noteAfterText || '');
          if (!tail) { warnings.push(`Note ${number}: target has no text`); continue; }
          const mark = document.createElement('sup');
          mark.className = 'ys-fn-mark';
          const link = document.createElement('a');
          link.className = 'ys-fn-ref';
          link.id = `${note.id}-ref-${++index}`;
          link.href = `#${note.id}`;
          link.textContent = number;
          link.dataset.noteOwner = this.id;
          link.setAttribute('role','doc-noteref');
          link.setAttribute('aria-label',`Lire la note ${number}${note.dataset.noteLabel ? ': ' + note.dataset.noteLabel : ''}`);
          mark.append(link);
          tail.append(mark);
          this.references.push(mark);
          if (back && index === 1) { back.href = `#${link.id}`; back.hidden = false; }
        }
      }
      const status = this.querySelector('[data-note-status]');
      if (status) { status.textContent = warnings.join(' · '); status.hidden = !warnings.length; }
      this.dataset.referenceCount = String(this.references.length);
      this.dataset.warningCount = String(warnings.length);
      this.dataset.ready = 'true';
    }

    navigate(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const ref = event.target.closest?.('a.ys-fn-ref');
      const back = event.target.closest?.('[data-note-back]');
      if ((!ref || ref.dataset.noteOwner !== this.id) && (!back || !this.contains(back))) return;
      const link = ref || back;
      const target = document.getElementById(link.hash.slice(1));
      if (!target) return;
      event.preventDefault();
      if (ref) {
        const returnLink = target.querySelector('[data-note-back]');
        if (returnLink) returnLink.href = `#${ref.id}`;
      }
      // Set focus as well as scroll so keyboard users can continue from the destination.
      const nav = document.querySelector('[data-ys-nav-bar].is-fixed');
      const navBottom = nav?.getBoundingClientRect().bottom || 0;
      const offset = Math.max(160, navBottom + 24);
      target.style.scrollMarginTop = `${offset}px`;
      history.pushState(null,'',link.hash);
      target.focus({preventScroll:true});
      target.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }
  }
  customElements.define('ys-t5-footnotes', T5Footnotes);
})();
